import { binarySideOf, type BinarySide } from '@/client/utils/binary-option-words';

type BinaryOptionLike = { optionName: string; optionAddress: string; description?: string };

export type BinaryOptionPair<T> = { yes?: T; no?: T };
type Side = BinarySide;

// Known semantic pairs live in utils/binary-option-words (shared with the
// position badge via option-tone). Yes words take the yes (positive) side and its
// colour, no words the no side. Whole-word match only ("Upload" is not "up").
const sideOf = binarySideOf;

const pair = <T>(optionList: T[], side: (option: T) => Side | undefined): BinaryOptionPair<T> | undefined => {
  const yes = optionList.find(o => side(o) === 'yes');
  const no = optionList.find(o => side(o) === 'no');
  return yes && no && yes !== no ? { yes, no } : undefined;
};

/**
 * Resolve the two sides of a binary market for the trade panel.
 *
 * - Only markets with exactly two options are binary; anything else returns an
 *   empty pair so multi-option flows are untouched.
 * - `optionName` is matched first against the known pairs (yes/no, option1/
 *   option2, up/down, higher/lower, above/below, over/under, long/short,
 *   rise/fall, 漲/跌), so
 *   colours follow meaning rather than array order. Yes/No keeps its mapping.
 * - If the names do not resolve, each option's `description` is tried as plain
 *   text. Market API descriptions are plain strings (translated at render time
 *   by ApiTranslation), not i18n JSON; DPIN Up/Down markets carry the word in
 *   `optionName` (admin option title), so this is a secondary path.
 * - If neither resolves a pair, fall back to array order: first option = yes
 *   side, second = no side.
 * - The two sides never resolve to the same option.
 */
export const resolveBinaryOptions = <T extends BinaryOptionLike>(optionList: T[]): BinaryOptionPair<T> => {
  if (optionList.length !== 2) return { yes: undefined, no: undefined };

  const [first, second] = optionList;
  if (first.optionAddress.toLowerCase() === second.optionAddress.toLowerCase()) {
    return { yes: undefined, no: undefined };
  }

  return (
    pair(optionList, o => sideOf(o.optionName)) ??
    pair(optionList, o => sideOf(o.optionName) ?? sideOf(o.description)) ?? { yes: first, no: second }
  );
};
